import { GRANTS } from "./permissionTypes";

export const ROLE_NAME_BY_ID = Object.freeze({
  CEO: "ceo",
  VP: "vp",
  HR: "hr",
  Manager: "manager",
  CSM: "csm",
  "Team Leader": "team_leader",
  Customer: "customer",
  Agent: "agent",
  Recruiter: "recruiter",
  Applicant: "applicant",
});

/**
 * Normalizes a role value.
 *
 * @param {number|string|object} role
 * @returns {string}
 */
export const normalizeRole = (role) => {
  const value =
    typeof role === "object"
      ? role?.name ?? role?.roleName ?? role?.id
      : role;

  if (ROLE_NAME_BY_ID[value]) {
    return ROLE_NAME_BY_ID[value];
  }

  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replaceAll(" ", "_");
};

/**
 * Normalizes an API endpoint.
 *
 * Prevents mismatches caused by trailing slashes.
 *
 * Example:
 *
 * /api/v1/managers/
 *
 * becomes:
 *
 * /api/v1/managers
 *
 * @param {string} endpoint
 * @returns {string}
 */
export const normalizeEndpoint = (endpoint) => {
  const normalized = String(endpoint ?? "").trim();

  if (
    normalized.length > 1 &&
    normalized.endsWith("/")
  ) {
    return normalized.slice(0, -1);
  }

  return normalized;
};

/**
 * Generates the permission map key.
 *
 * Example:
 *
 * GET /api/v1/managers
 *
 * @param {string} endpoint
 * @param {string} method
 * @returns {string}
 */
export const permissionKey = (endpoint, method) =>
  `${String(method ?? "").toUpperCase()} ${normalizeEndpoint(endpoint)}`;

/**
 * Builds a permission map using the permissions returned
 * by the API for the currently authenticated user.
 *
 * API response example:
 *
 * {
 *   endpoint: "/api/v1/managers",
 *   method: "GET",
 *   accessLevel: "full"
 * }
 *
 * Result:
 *
 * Map {
 *   "GET /api/v1/managers" => "full"
 * }
 *
 * @param {Array} permissions
 * @returns {Map}
 */
export const buildPermissionMap = (permissions) => {
  return new Map(
    (permissions ?? []).map((permission) => [
      permissionKey(
        permission.endpoint,
        permission.method
      ),
      permission.accessLevel ?? null,
    ])
  );
};

/**
 * Compares two IDs.
 *
 * @param {*} left
 * @param {*} right
 * @returns {boolean}
 */
const sameId = (left, right) =>
  left != null &&
  right != null &&
  String(left) === String(right);

/**
 * Evaluates a grant/access level against the provided scope.
 *
 * @param {string|null} grant
 * @param {object} scope
 * @returns {boolean}
 */
export const evaluateGrant = (
  grant,
  scope = {}
) => {
  if (!grant) {
    return false;
  }

  /**
   * Full access.
   */
  if (grant === GRANTS.FULL) {
    return true;
  }

  /**
   * User can only access their own resources.
   */
  if (grant === GRANTS.OWNERSHIP) {
    return sameId(
      scope.currentUserId,
      scope.ownerId
    );
  }

  /**
   * User can access resources belonging
   * to one of their allowed projects.
   */
  if (grant === GRANTS.PROJECT_SCOPED) {
    return (
      scope.allowedProjectIds ?? []
    ).some((id) =>
      sameId(id, scope.projectId)
    );
  }

  return false;
};